import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import type { BlockyStrikerWebViewRef } from '../../../src/quiz/hooks/useBlockyStrikerGame';

interface Props {
  html: string;
  onMessage: (event: { nativeEvent: { data: string } }) => void;
}

/**
 * Web host: the game runs in a same-origin `srcDoc` iframe. The game posts to `window.parent`; we forward
 * those as `{ nativeEvent: { data } }` so the hook's handler is platform-agnostic.
 */
export const BlockyStrikerWebView = forwardRef<BlockyStrikerWebViewRef, Props>(function BlockyStrikerWebView({ html, onMessage }, ref) {
  const frameRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(ref, () => ({
    reload: () => {
      // Re-running srcDoc reboots the runtime (re-posts `ready`).
      const el = frameRef.current;
      if (el) { const doc = el.srcdoc; el.srcdoc = ''; el.srcdoc = doc; }
    },
  }), []);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.source !== frameRef.current?.contentWindow) return;
      if (typeof e.data !== 'string') return;
      onMessage({ nativeEvent: { data: e.data } });
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onMessage]);

  return (
    <iframe
      ref={frameRef}
      title="Blocky Striker"
      srcDoc={html}
      style={{ flex: 1, width: '100%', height: '100%', border: 'none', background: 'transparent' }}
    />
  );
});
